/* eslint-disable camelcase */
import { useState, useEffect, useRef } from 'react'
import { withAppHeaders, withContentType, toJSON } from '@ally/fido'
import { flow } from '@ally/utilitarian'
import { HostServices } from '@ally/federated-types'

import { FocusDebtAccount } from '../types'
import { DebtPlanFocusAccountValue } from './DebtPlanFocusAccountContext'

export type FetchHookState<T> = {
  isLoaded: boolean
  isLoading: boolean
  isError: boolean
  data: T | null
}

function useGetDebtPlanFocusAccount(
  services: HostServices,
): DebtPlanFocusAccountValue {
  const showDebtPlan =
    services.featureFlags.variation('FF_debt-plan-snapshot', false) &&
    !!(
      services.session.data?.relationships?.tools?.debtReduction?.status ===
      'ACTIVE'
    )
  const { log, session } = services
  const access_token = session.data?.access_token

  const [state, setState] = useState<DebtPlanFocusAccountValue>({
    data: null,
    isError: false,
    isLoading: true,
    isLoaded: false,
  })

  // make sure only one fetch occurs
  const hasFetchStarted = useRef(false)

  // TODO use library? react-query? use-swr? another?
  useEffect(() => {
    async function getFocusAccount(): Promise<void> {
      const response = await fetch(
        '/acs/debt-plan-svc/debt-accounts/focus',
        flow(
          withAppHeaders,
          withContentType,
        )({
          headers: {
            // /sts token for trasnmit v2 API's
            Authorization: `Bearer ${access_token}`,
          },
        }),
      )

      const data = await toJSON<FocusDebtAccount>(response)

      // check no id property in the case of some weird scenario. Fixes apparition scenarios that don't use debtPlan's too.
      if (!response.ok || !data.id) {
        setState({
          isLoaded: true,
          isLoading: false,
          isError: true,
          data: null,
        })
        log.error({
          message: { error: data, status: response.status },
        })
        return
      }

      setState({
        isLoaded: true,
        isLoading: false,
        isError: false,
        data,
      })
    }

    // for any non debt plan user
    if (!showDebtPlan) {
      setState({
        isLoaded: true,
        isLoading: false,
        isError: false,
        data: null,
      })
      return
    }

    if (
      // this check is also done at the LOB render level
      showDebtPlan &&
      access_token &&
      !state.isLoaded &&
      !hasFetchStarted.current
    ) {
      try {
        hasFetchStarted.current = true
        getFocusAccount()
      } catch (error) /* istanbul ignore next */ {
        setState({
          isLoaded: true,
          isLoading: false,
          isError: true,
          data: null,
        })
        log.error({
          message: {
            error: 'Unexpected Error when fetching debt plan focus account.',
          },
        })
      }
    }
  }, [access_token, showDebtPlan, log, state.isLoaded])

  return state
}

export default useGetDebtPlanFocusAccount
